import { useState, useEffect, useCallback } from "react";
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { type Address, formatUnits, zeroAddress } from "viem";
import { Plus, Minus, Loader2, ExternalLink } from "lucide-react";
import { ADDRESSES, V2_ROUTER_ABI, V2_FACTORY_ABI, V2_PAIR_ABI } from "../../config/contracts";
import { type TokenInfo, NATIVE_JASMY, DEFAULT_TOKEN_LIST } from "../../config/tokens";
import { TokenSelector } from "../swap/TokenSelector";
import { useTokenBalance } from "../../hooks/useTokenBalance";
import { useTokenAllowance } from "../../hooks/useTokenAllowance";
import { formatTokenAmount, getDeadline, parseTokenAmount, explorerUrl, cn } from "../../lib/utils";

type Tab = "add" | "remove";

export function LiquidityCard() {
  const { address: account, isConnected } = useAccount();
  const [tab, setTab] = useState<Tab>("add");
  const [tokenA, setTokenA] = useState<TokenInfo>(NATIVE_JASMY);
  const [tokenB, setTokenB] = useState<TokenInfo>(DEFAULT_TOKEN_LIST[1]);
  const [amountA, setAmountA] = useState("");
  const [amountB, setAmountB] = useState("");
  const [removePercent, setRemovePercent] = useState(50);
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();

  const { balance: balanceA } = useTokenBalance(tokenA.isNative ? undefined : tokenA.address);
  const { balance: balanceB } = useTokenBalance(tokenB.isNative ? undefined : tokenB.address);

  const addressA = tokenA.isNative ? ADDRESSES.WJASMY : tokenA.address;
  const addressB = tokenB.isNative ? ADDRESSES.WJASMY : tokenB.address;

  const hasNative = tokenA.isNative || tokenB.isNative;

  // Query pair address from factory
  const { data: pairAddress } = useReadContract({
    address: ADDRESSES.V2_FACTORY,
    abi: V2_FACTORY_ABI,
    functionName: "getPair",
    args: [addressA, addressB],
    query: { enabled: addressA !== addressB, refetchInterval: 15_000 },
  });

  const pair = pairAddress as Address | undefined;
  const hasPair = pair && pair !== zeroAddress;

  // Read pair reserves
  const { data: reserves } = useReadContract({
    address: pair ?? zeroAddress,
    abi: V2_PAIR_ABI,
    functionName: "getReserves",
    query: { enabled: !!hasPair, refetchInterval: 10_000 },
  });

  // Read token0 to know which reserve is which
  const { data: token0Address } = useReadContract({
    address: pair ?? zeroAddress,
    abi: V2_PAIR_ABI,
    functionName: "token0",
    query: { enabled: !!hasPair, staleTime: Infinity },
  });

  // Read LP total supply
  const { data: lpTotalSupply } = useReadContract({
    address: pair ?? zeroAddress,
    abi: V2_PAIR_ABI,
    functionName: "totalSupply",
    query: { enabled: !!hasPair, refetchInterval: 10_000 },
  });

  // Read user LP balance
  const { data: lpBalance, refetch: refetchLpBalance } = useReadContract({
    address: pair ?? zeroAddress,
    abi: V2_PAIR_ABI,
    functionName: "balanceOf",
    args: account ? [account] : undefined,
    query: { enabled: !!hasPair && !!account, refetchInterval: 10_000 },
  });

  const reserveData = reserves as [bigint, bigint, number] | undefined;
  const isToken0A = token0Address ? (token0Address as Address).toLowerCase() === addressA.toLowerCase() : true;

  const reserveA = reserveData ? (isToken0A ? reserveData[0] : reserveData[1]) : 0n;
  const reserveB = reserveData ? (isToken0A ? reserveData[1] : reserveData[0]) : 0n;

  const parsedAmountA = parseTokenAmount(amountA, tokenA.decimals);
  const parsedAmountB = parseTokenAmount(amountB, tokenB.decimals);

  // Auto-compute Token B amount from reserves ratio
  useEffect(() => {
    if (reserveA > 0n && reserveB > 0n && parsedAmountA > 0n) {
      const computedB = (parsedAmountA * reserveB) / reserveA;
      const formatted = formatUnits(computedB, tokenB.decimals);
      setAmountB(formatted);
    }
  }, [parsedAmountA, reserveA, reserveB, tokenB.decimals]);

  // Approval hooks for Token A and Token B (skip native)
  const { needsApproval: needsApprovalA, approve: approveA, isApproving: isApprovingA } =
    useTokenAllowance(
      tokenA.isNative ? undefined : tokenA.address,
      ADDRESSES.V2_ROUTER,
      parsedAmountA
    );

  const { needsApproval: needsApprovalB, approve: approveB, isApproving: isApprovingB } =
    useTokenAllowance(
      tokenB.isNative ? undefined : tokenB.address,
      ADDRESSES.V2_ROUTER,
      parsedAmountB
    );

  // LP to remove based on slider
  const lpToRemove =
    (lpBalance as bigint) && removePercent > 0
      ? ((lpBalance as bigint) * BigInt(removePercent)) / 100n
      : 0n;

  // Approval for LP token removal
  const { needsApproval: needsLpApproval, approve: approveLp, isApproving: isApprovingLp } =
    useTokenAllowance(pair ?? undefined, ADDRESSES.V2_ROUTER, lpToRemove);

  const { writeContract, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  // Reset form on success
  useEffect(() => {
    if (isSuccess) {
      setAmountA("");
      setAmountB("");
      setTxHash(undefined);
      refetchLpBalance();
    }
  }, [isSuccess, refetchLpBalance]);

  // Add liquidity handler -- supports both native JASMY and token-to-token
  const addLiquidity = useCallback(() => {
    if (!account) return;
    const deadline = getDeadline(20);
    const minA = (parsedAmountA * 95n) / 100n;
    const minB = (parsedAmountB * 95n) / 100n;

    if (hasNative) {
      // One side is native JASMY: use addLiquidityJASMY
      const nativeSideIsA = tokenA.isNative;
      const tokenSideAddr = nativeSideIsA ? tokenB.address : tokenA.address;
      const tokenDesired = nativeSideIsA ? parsedAmountB : parsedAmountA;
      const tokenMin = nativeSideIsA ? minB : minA;
      const jasmyMin = nativeSideIsA ? minA : minB;
      const nativeValue = nativeSideIsA ? parsedAmountA : parsedAmountB;

      writeContract(
        {
          address: ADDRESSES.V2_ROUTER,
          abi: V2_ROUTER_ABI,
          functionName: "addLiquidityJASMY",
          args: [tokenSideAddr, tokenDesired, tokenMin, jasmyMin, account, deadline],
          value: nativeValue,
        },
        { onSuccess: (h) => setTxHash(h) }
      );
    } else {
      // Both sides are ERC20 tokens: use addLiquidity
      writeContract(
        {
          address: ADDRESSES.V2_ROUTER,
          abi: V2_ROUTER_ABI,
          functionName: "addLiquidity",
          args: [tokenA.address, tokenB.address, parsedAmountA, parsedAmountB, minA, minB, account, deadline],
        },
        { onSuccess: (h) => setTxHash(h) }
      );
    }
  }, [account, parsedAmountA, parsedAmountB, tokenA, tokenB, hasNative, writeContract]);

  // Remove liquidity handler -- supports both native JASMY and token-to-token
  const removeLiquidity = useCallback(() => {
    if (!account || lpToRemove === 0n) return;
    const deadline = getDeadline(20);

    if (hasNative) {
      // One side is native: use removeLiquidityJASMY
      const nativeToken = tokenA.isNative ? tokenB : tokenA;

      writeContract(
        {
          address: ADDRESSES.V2_ROUTER,
          abi: V2_ROUTER_ABI,
          functionName: "removeLiquidityJASMY",
          args: [nativeToken.address, lpToRemove, 0n, 0n, account, deadline],
        },
        { onSuccess: (h) => setTxHash(h) }
      );
    } else {
      // Both sides are ERC20: use removeLiquidity
      writeContract(
        {
          address: ADDRESSES.V2_ROUTER,
          abi: V2_ROUTER_ABI,
          functionName: "removeLiquidity",
          args: [tokenA.address, tokenB.address, lpToRemove, 0n, 0n, account, deadline],
        },
        { onSuccess: (h) => setTxHash(h) }
      );
    }
  }, [account, lpToRemove, tokenA, tokenB, hasNative, writeContract]);

  const isLoading = isPending || isConfirming;

  const isNewPool = hasPair && reserveA === 0n && reserveB === 0n;
  const isFirstLiquidity = !hasPair || isNewPool;

  function getButtonText(): string {
    if (!isConnected) return "Connect Wallet";
    if (tab === "add") {
      if (!amountA || parseFloat(amountA) <= 0) return "Enter Amount";
      if (isFirstLiquidity && (!amountB || parseFloat(amountB) <= 0)) return "Enter Both Amounts";
      if (isApprovingA) return `Approving ${tokenA.symbol}...`;
      if (needsApprovalA) return `Approve ${tokenA.symbol}`;
      if (isApprovingB) return `Approving ${tokenB.symbol}...`;
      if (needsApprovalB) return `Approve ${tokenB.symbol}`;
      if (isPending) return "Confirming...";
      if (isConfirming) return "Adding Liquidity...";
      return "Add Liquidity";
    } else {
      if (!(lpBalance as bigint) || (lpBalance as bigint) === 0n) return "No LP Tokens";
      if (isApprovingLp) return "Approving LP...";
      if (needsLpApproval) return "Approve LP Tokens";
      if (isPending) return "Confirming...";
      if (isConfirming) return "Removing Liquidity...";
      return "Remove Liquidity";
    }
  }

  function handleAction() {
    if (tab === "add") {
      if (needsApprovalA) return approveA();
      if (needsApprovalB) return approveB();
      addLiquidity();
    } else {
      if (needsLpApproval) return approveLp();
      removeLiquidity();
    }
  }

  // Calculate user pool share percentage
  const yourShare =
    lpBalance && lpTotalSupply && (lpTotalSupply as bigint) > 0n
      ? (Number((lpBalance as bigint) * 10000n / (lpTotalSupply as bigint)) / 100).toFixed(2)
      : "0.00";

  // Estimated tokens received on remove
  const estimatedRemoveA =
    lpToRemove > 0n && (lpTotalSupply as bigint) > 0n
      ? (lpToRemove * reserveA) / (lpTotalSupply as bigint)
      : 0n;
  const estimatedRemoveB =
    lpToRemove > 0n && (lpTotalSupply as bigint) > 0n
      ? (lpToRemove * reserveB) / (lpTotalSupply as bigint)
      : 0n;

  return (
    <div className="glass-card p-6 w-full max-w-[480px] glow-primary">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-semibold text-xl">Liquidity</h2>
      </div>

      <div className="flex gap-1 p-1 bg-surface/50 rounded-card mb-6">
        <button
          onClick={() => setTab("add")}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-button text-sm font-medium transition-all",
            tab === "add" ? "bg-primary/15 text-primary" : "text-text-secondary hover:text-text-primary"
          )}
        >
          <Plus className="w-4 h-4" /> Add
        </button>
        <button
          onClick={() => setTab("remove")}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-button text-sm font-medium transition-all",
            tab === "remove" ? "bg-primary/15 text-primary" : "text-text-secondary hover:text-text-primary"
          )}
        >
          <Minus className="w-4 h-4" /> Remove
        </button>
      </div>

      {tab === "add" ? (
        <div className="space-y-3">
          <div className="token-input-row">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-tertiary">Token A</span>
              <button
                onClick={() => setAmountA(formatUnits(balanceA, tokenA.decimals))}
                className="text-xs text-text-tertiary hover:text-primary transition-colors"
              >
                Balance: {formatTokenAmount(balanceA, tokenA.decimals)}
                <span className="ml-1 text-primary font-medium">MAX</span>
              </button>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={amountA}
                onChange={(e) => setAmountA(e.target.value)}
                placeholder="0.0"
                className="amount-input"
                min="0"
                step="any"
              />
              <TokenSelector selectedToken={tokenA} onSelect={setTokenA} otherToken={tokenB} />
            </div>
          </div>

          <div className="flex justify-center">
            <Plus className="w-5 h-5 text-text-tertiary" />
          </div>

          <div className="token-input-row">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-tertiary">Token B</span>
              <span className="text-xs text-text-tertiary">
                Balance: {formatTokenAmount(balanceB, tokenB.decimals)}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={amountB}
                onChange={(e) => setAmountB(e.target.value)}
                placeholder="0.0"
                className="amount-input"
                min="0"
                step="any"
                readOnly={hasPair && reserveA > 0n && reserveB > 0n}
              />
              <TokenSelector selectedToken={tokenB} onSelect={setTokenB} otherToken={tokenA} />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="token-input-row">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-text-secondary">Amount to Remove</span>
              <span className="text-2xl font-bold text-text-primary">{removePercent}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={removePercent}
              onChange={(e) => setRemovePercent(parseInt(e.target.value))}
              className="w-full accent-primary"
            />
            <div className="flex gap-2 mt-3">
              {[25, 50, 75, 100].map((pct) => (
                <button
                  key={pct}
                  onClick={() => setRemovePercent(pct)}
                  className={cn(
                    "flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all",
                    removePercent === pct
                      ? "bg-primary/15 text-primary border-primary/30"
                      : "bg-surface border-border text-text-secondary hover:text-text-primary"
                  )}
                >
                  {pct}%
                </button>
              ))}
            </div>
          </div>

          <div className="text-sm text-text-secondary">
            <div className="flex justify-between py-1">
              <span>Your LP Balance</span>
              <span>{formatTokenAmount((lpBalance as bigint) ?? 0n)} LP</span>
            </div>
            <div className="flex justify-between py-1">
              <span>LP to Remove</span>
              <span>{formatTokenAmount(lpToRemove)} LP</span>
            </div>
            {hasPair && lpToRemove > 0n && (
              <>
                <div className="flex justify-between py-1 text-text-tertiary">
                  <span>You Receive ({tokenA.symbol})</span>
                  <span>{formatTokenAmount(estimatedRemoveA, tokenA.decimals)}</span>
                </div>
                <div className="flex justify-between py-1 text-text-tertiary">
                  <span>You Receive ({tokenB.symbol})</span>
                  <span>{formatTokenAmount(estimatedRemoveB, tokenB.decimals)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isFirstLiquidity && tab === "add" && (
        <div className="mt-4 p-3 bg-primary/5 rounded-button border border-primary/20 text-sm text-primary">
          You are the first liquidity provider. The ratio of tokens you add sets the initial price.
        </div>
      )}

      {hasPair && !isNewPool && (
        <div className="mt-4 p-3 bg-surface/30 rounded-button border border-border text-sm">
          <div className="text-text-tertiary text-xs mb-2">Pool Info</div>
          <div className="flex justify-between text-text-secondary py-0.5">
            <span>{tokenA.symbol} Reserve</span>
            <span>{formatTokenAmount(reserveA, tokenA.decimals)}</span>
          </div>
          <div className="flex justify-between text-text-secondary py-0.5">
            <span>{tokenB.symbol} Reserve</span>
            <span>{formatTokenAmount(reserveB, tokenB.decimals)}</span>
          </div>
          <div className="flex justify-between text-text-secondary py-0.5">
            <span>Your Pool Share</span>
            <span>{yourShare}%</span>
          </div>
          {reserveA > 0n && reserveB > 0n && (
            <div className="flex justify-between text-text-secondary py-0.5">
              <span>Rate</span>
              <span>
                1 {tokenA.symbol} = {(
                  parseFloat(formatUnits(reserveB, tokenB.decimals)) /
                  parseFloat(formatUnits(reserveA, tokenA.decimals))
                ).toFixed(6)} {tokenB.symbol}
              </span>
            </div>
          )}
        </div>
      )}

      <button
        onClick={handleAction}
        disabled={!isConnected || isLoading || isApprovingA || isApprovingB || isApprovingLp}
        className="btn-primary w-full mt-4 flex items-center justify-center gap-2"
      >
        {(isLoading || isApprovingA || isApprovingB || isApprovingLp) && (
          <Loader2 className="w-4 h-4 animate-spin" />
        )}
        {getButtonText()}
      </button>

      {txHash && (
        <div className="mt-3 text-center">
          <a
            href={explorerUrl("tx", txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
          >
            View Transaction <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
}
